import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BringzzImage, BringzzButton, BringzzIcon } from "@bringzz/components";
import { ShimmerImage } from './Shimmer';
import ImageGalleryModal from './ImageGalleryModal';

const PhotosTab = ({
  loading,
  error,
  isOwner,
  handleGalleryChange,
  uploading,
  displayImages,
  removeGalleryImage
}) => {
  const { t } = useTranslation('pharmacyProfile');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const sortedImages = useMemo(() => {
    return displayImages || [];
  }, [displayImages]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-1 p-1">
        {Array.from({ length: 6 }).map((_, index) => (
          <ShimmerImage key={index} className="aspect-square w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        {t('tabs.photos.failed_to_load')}
      </div>
    );
  }

  if (sortedImages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <BringzzIcon
          icon="IconImage"
          folder="LineIcons"
          size="48"
          className="text-gray-400 mb-4"
        />
        <p className="text-gray-500 mb-4">{t('tabs.photos.no_photos')}</p>
        {isOwner && (
          <label htmlFor="gallery-upload" className="cursor-pointer">
            <BringzzButton
              as="span"
              icon={{
                variant: "IconPlus",
                folder: "LineIcons",
                size: "16"
              }}
              disabled={uploading}
              loading={uploading}
            >
              {t('tabs.photos.add_photos')}
            </BringzzButton>
          </label>
        )}
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleGalleryChange}
          className="hidden"
          id="gallery-upload"
          disabled={uploading}
        />
      </div>
    );
  }

  return (
    <div className="pb-24 md:pb-4">
      <div className="grid grid-cols-3 gap-1 p-1">
        {sortedImages.map((image, index) => (
          <div key={index} className="relative aspect-square group">
            <BringzzImage
              src={image}
              alt={`Gallery image ${index + 1}`}
              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => handleImageClick(index)}
            />
            {isOwner && (
              <button
                className="absolute top-1 right-1 bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  removeGalleryImage(index);
                }}
                disabled={uploading}
              >
                <BringzzIcon
                  size="12"
                  icon="IconMinus"
                  folder="LineIcons"
                  className="text-white"
                />
              </button>
            )}
          </div>
        ))}
      </div>

      {isOwner && (
        <div className="p-4 flex justify-center">
          <label htmlFor="gallery-upload" className="cursor-pointer">
            <BringzzButton
              as="span"
              icon={{
                variant: "IconPlus",
                folder: "LineIcons",
                size: "16"
              }}
              disabled={uploading}
              loading={uploading}
            >
              {t('tabs.photos.add_photos')}
            </BringzzButton>
          </label>
        </div>
      )}
      
      <input
        type="file"
        multiple
        accept="image/*"
        onChange={handleGalleryChange}
        className="hidden"
        id="gallery-upload"
        disabled={uploading}
      />

      <ImageGalleryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        images={sortedImages}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
};

export default PhotosTab; 